// src/components/admin/performance/PendingReviewsTable.js
import React, { useState, useEffect } from 'react';
import { Loader, AlertCircle, Star, Calendar, User, CheckCircle, Clock, Edit } from 'lucide-react';
import performanceService from '../../../services/performance.service';
import ReviewConfirmationModal from './ReviewConfirmationModal';

const PendingReviewsTable = () => {
    const [pendingReviews, setPendingReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedReview, setSelectedReview] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);

    const fetchPendingReviews = async () => {
        try {
            setLoading(true);
            setError(null);
            const reviews = await performanceService.getPendingReviews();
            setPendingReviews(reviews);
        } catch (err) {
            console.error('Error fetching pending reviews:', err);
            setError('Failed to load pending reviews');
            setPendingReviews([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPendingReviews();
    }, []);

    const handleReviewClick = (review) => {
        setSelectedReview(review);
        setShowConfirmModal(true);
    };

    const handleCloseModal = () => {
        setShowConfirmModal(false);
        setSelectedReview(null);
        fetchPendingReviews(); // Refresh the list
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center py-12">
                <Loader className="w-8 h-8 animate-spin text-blue-600 dark:text-blue-400" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
            </div>
        );
    }

    if (pendingReviews.length === 0) {
        return (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-12 text-center">
                <CheckCircle className="w-16 h-16 text-green-500 dark:text-green-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">All Caught Up!</h3>
                <p className="text-gray-600 dark:text-gray-400">
                    There are no pending reviews waiting for your confirmation.
                </p>
            </div>
        );
    }

    return (
        <>
            <div className="tour-performance-pending-list bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                                Pending Reviews
                            </h2>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                Review and confirm self-reviews and manager evaluations before finalizing
                            </p>
                        </div>
                        <span className="px-3 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 text-sm font-semibold rounded-full">
                            {pendingReviews.length} Pending
                        </span>
                    </div>
                </div>

                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                    {pendingReviews.map((review) => (
                        <div
                            key={review.id}
                            className="p-6 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer"
                            onClick={() => handleReviewClick(review)}
                        >
                            <div className="flex items-start justify-between">
                                <div className="flex-1">
                                    {/* Employee Info */}
                                    <div className="flex items-center gap-3 mb-3">
                                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-sm font-bold">
                                            {review.first_name?.[0]}{review.last_name?.[0]}
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2">
                                                <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                                                    {review.first_name} {review.last_name}
                                                </h3>
                                                {/* Review Type Badge */}
                                                {review.review_type === 'self_review' ? (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300">
                                                        Self Review
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                                                        Manager Review
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                                {review.designation}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Review Details Grid */}
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                                        <div className="flex items-center gap-2">
                                            <User className="w-4 h-4 text-gray-400" />
                                            <div>
                                                <p className="text-xs text-gray-500 dark:text-gray-400">Reviewed By</p>
                                                <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                    {review.reviewer_first_name} {review.reviewer_last_name}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-2">
                                            <Calendar className="w-4 h-4 text-gray-400" />
                                            <div>
                                                <p className="text-xs text-gray-500 dark:text-gray-400">Review Cycle</p>
                                                <p className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                                                    {review.review_cycle}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-2">
                                            <Star className="w-4 h-4 text-yellow-400" />
                                            <div>
                                                <p className="text-xs text-gray-500 dark:text-gray-400">Overall Rating</p>
                                                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                                                    {parseFloat(review.overall_rating).toFixed(1)}/5.0
                                                </p>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-2">
                                            <Clock className="w-4 h-4 text-gray-400" />
                                            <div>
                                                <p className="text-xs text-gray-500 dark:text-gray-400">Submitted</p>
                                                <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                    {new Date(review.submitted_to_admin_at).toLocaleDateString()}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Review Period */}
                                    <div className="text-sm text-gray-600 dark:text-gray-400">
                                        <span className="font-medium">Period:</span>{' '}
                                        {new Date(review.review_period_start).toLocaleDateString()} -{' '}
                                        {new Date(review.review_period_end).toLocaleDateString()}
                                    </div>

                                    {/* Skill Ratings Summary */}
                                    <div className="mt-3 flex items-center gap-4 text-xs text-gray-600 dark:text-gray-400">
                                        <span>Tech: {review.technical_skills}/5</span>
                                        <span>Comm: {review.communication_skills}/5</span>
                                        <span>Team: {review.teamwork}/5</span>
                                        <span>Problem: {review.problem_solving}/5</span>
                                        <span>Leader: {review.leadership}/5</span>
                                    </div>
                                </div>

                                {/* Action Button */}
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleReviewClick(review);
                                    }}
                                    className="ml-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium flex items-center gap-2"
                                >
                                    <Edit className="w-4 h-4" />
                                    Review & Confirm
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Review Confirmation Modal */}
            {showConfirmModal && selectedReview && (
                <ReviewConfirmationModal
                    isOpen={showConfirmModal}
                    onClose={handleCloseModal}
                    review={selectedReview}
                />
            )}
        </>
    );
};

export default PendingReviewsTable;
